import React, { useRef } from 'react';
import { useModalFocus } from './useModalFocus';
import { nocookieEmbedUrl, youtubeWatchUrl, parseYouTubeId } from './helpVideos';

/**
 * The ONE place a third-party frame is ever mounted: the privacy-enhanced
 * YouTube player, inside the app's modal contract, only after a click.
 * Closing (button, Esc, backdrop) unmounts the component → the iframe is
 * gone and nothing from Google remains on the page.
 *
 * Props: { video: { source, url|path, title }, onClose, returnFocusTo }.
 * Only the 'youtube' branch exists in v1; other sources render nothing.
 */
export default function HelpVideoPlayer({ video, onClose, returnFocusTo }) {
  const cardRef = useRef(null);
  const closeRef = useRef(null);
  useModalFocus({ cardRef, initialRef: closeRef, onEscape: onClose, returnTo: returnFocusTo });

  if (!video || video.source !== 'youtube') return null;
  const id = video.videoId || parseYouTubeId(video.url);
  if (!id) return null;
  const title = video.title || 'How-to video';

  return (
    <div
      className="modal-overlay help-video-player-overlay"
      data-app-modal="help-video-player"
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px',
      }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="modal-content help-video-player"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0f172a', color: 'white', borderRadius: '12px', padding: '12px',
          width: '100%', maxWidth: '860px', boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'white' }}>{title}</h3>
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            aria-label="Close video"
            title="Close"
            className="help-video-close"
            style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.6em', cursor: 'pointer', minWidth: '44px', minHeight: '44px' }}
          >
            &times;
          </button>
        </div>
        <div className="help-video-frame" style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#000', borderRadius: '8px', overflow: 'hidden' }}>
          <iframe
            src={nocookieEmbedUrl(id)}
            title={title}
            allow="autoplay; fullscreen; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          />
        </div>
        <div style={{ marginTop: '10px', fontSize: '0.9em', textAlign: 'center' }}>
          <a
            href={youtubeWatchUrl(id)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#93c5fd' }}
          >
            Open on YouTube
          </a>
          <span style={{ color: '#94a3b8' }}> — if the player doesn&apos;t load here</span>
        </div>
      </div>
    </div>
  );
}
